// @flow

import { featureIsEnabled } from '@ta/common.features';
import {
  installSentry,
  sendSentryRequest,
  type SentryError,
  type SentryProject,
} from '@ta/platform.sentry';
import type { DebugContext, EnvironmentContext } from '@ta/lithium.context';

export { LOGGER_COMMON_LOGGING } from '@ta/platform.sentry';

/**
 * NOTE: Please use the logging methods in logging.jsx rather than calling Sentry methods directly.
 * If you are interested in providing the additional info the sentry logger accepts, please consider ErrorBoundary.
 */

let installedSentry;
const queuedErrors: SentryError[] = [];

/**
 * Defines sentry for consumption by @ta/common.logging.
 */
export const defineSentry = (
  debug: DebugContext,
  pageName: string,
  environment: EnvironmentContext,
  project: SentryProject,
): Promise<any> => {
  if (!featureIsEnabled('sentryio_js') || installedSentry) {
    return Promise.resolve();
  }

  return installSentry(debug, pageName, environment, project).then(Sentry => {
    installedSentry = Sentry;
    queuedErrors.forEach(entry => sendSentryRequest(Sentry, entry));
    queuedErrors.length = 0;

    // AMD define sentry so that it can be used in pre-web-components code.
    if (LEGACY_AMD_SUPPORT) {
      // you can provide a plain object as the 3rd argument in the AMD API, but rollupAmdShim.js does not support it :(
      // eslint-disable-next-line no-undef
      define('@ta/sentryBridge', [], () => Sentry);
    }
  });
};

/** Queues up errors to be replayed once Sentry is defined. */
const queueError = (sentryError: SentryError) => {
  queuedErrors.push(sentryError);
};

/** Not intended to be called directly. Best to call @ta/common.logging.error or use the ErrorBoundary component. */
export const logToSentry = (sentryError: SentryError) => {
  if (!featureIsEnabled('sentryio_js')) {
    return;
  }

  if (installedSentry) {
    sendSentryRequest(installedSentry, sentryError);
  } else {
    queueError(sentryError);
  }
};
